import { memo, useCallback, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { useUserId } from '@/auth/UserIdContext';
import { ResizeHandle } from '@/components/ResizeHandle';
import { useResizePanel } from '@/hooks/useResizePanel';
import { logger } from '@/logger';
import { AudienceDetail } from '@/spaces/audiences/AudienceDetail';
import { AudiencesList } from '@/spaces/audiences/AudiencesList';
import type { Audience, AudiencePatch } from '@/spaces/audiences/types';
import { useAudiencesTab } from '@/spaces/audiences/useAudiencesTab';
import { ContactDetail } from '@/spaces/contacts/ContactDetail';
import { ContactsList } from '@/spaces/contacts/ContactsList';
import { ImportContactsDialog } from '@/spaces/contacts/ImportContactsDialog';
import type { Contact } from '@/spaces/contacts/types';
import { useContactsTab } from '@/spaces/contacts/useContactsTab';
import { getDisplayName } from '@/spaces/contacts/utils';
import { DetailPanelCard } from '@/spaces/people/DetailPanelCard';
import { PeopleTabsHeader } from '@/spaces/people/PeopleTabsHeader';
import { cn } from '@/utils/cn.ts';
import { trackEvent } from '@/utils/fullstory';

type PanelKind = 'contact' | 'audience' | null;

export const PeopleSpace = memo(function PeopleSpace() {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();

	const currentSpace = bridgeApi.user_state?.current_space;
	const activeTab: 'contacts' | 'audiences' =
		currentSpace?.type === 'audiences' ? 'audiences' : 'contacts';

	const [selectedContactId, setSelectedContactId] = useState<string | null>(
		() =>
			currentSpace?.type === 'contacts'
				? (currentSpace.selected_contact_id ?? null)
				: null,
	);
	const [selectedAudienceId, setSelectedAudienceId] = useState<string | null>(
		() =>
			currentSpace?.type === 'audiences'
				? (currentSpace.selected_audience_id ?? null)
				: null,
	);
	const [draftContact, setDraftContact] = useState<Contact | null>(null);
	const [draftAudience, setDraftAudience] = useState<Audience | null>(null);
	const [isCreatingAudienceFromSelection, setIsCreatingAudienceFromSelection] =
		useState(false);
	const [panelKind, setPanelKind] = useState<PanelKind>(() => {
		if (currentSpace?.type === 'contacts' && currentSpace.selected_contact_id) {
			return 'contact';
		}
		if (
			currentSpace?.type === 'audiences' &&
			currentSpace.selected_audience_id
		) {
			return 'audience';
		}
		return null;
	});

	const audienceMemberDeltaRef = useRef<
		(audienceId: string, delta: number) => void
	>(() => {});

	const contactsTab = useContactsTab({
		selectedContactId,
		onSelectContact: setSelectedContactId,
		draftContact,
		onDraftChange: setDraftContact,
		onAudienceMemberCountDelta: (audienceId, delta) =>
			audienceMemberDeltaRef.current(audienceId, delta),
	});
	const audiencesTab = useAudiencesTab({
		selectedAudienceId,
		onSelectAudience: setSelectedAudienceId,
		draftAudience,
		onDraftChange: setDraftAudience,
	});

	audienceMemberDeltaRef.current = (audienceId, delta) => {
		const audience = audiencesTab.list.items.find((a) => a.id === audienceId);
		if (!audience) return;
		audiencesTab.list.updateItem({
			...audience,
			member_count: Math.max(0, (audience.member_count ?? 0) + delta),
		});
	};

	const handleContactClick = useCallback(
		(contact: Contact) => {
			contactsTab.handleContactClick(contact);
			setPanelKind('contact');
		},
		[contactsTab],
	);

	const handleAudienceClick = useCallback(
		(audience: Audience) => {
			audiencesTab.handleAudienceClick(audience);
			setPanelKind('audience');
		},
		[audiencesTab],
	);

	const handleCreateContact = useCallback(() => {
		contactsTab.handleCreate();
		setPanelKind('contact');
	}, [contactsTab]);

	const handleCreateAudience = useCallback(() => {
		audiencesTab.handleCreate();
		setPanelKind('audience');
	}, [audiencesTab]);

	const handleOpenCreateAudienceFromContacts = useCallback(() => {
		trackEvent('Audience Creation From Selection Opened', {
			selected_count: contactsTab.selectedContactIds.size,
		});
		setIsCreatingAudienceFromSelection(true);
		audiencesTab.handleCreate();
		setPanelKind('audience');
	}, [audiencesTab, contactsTab.selectedContactIds]);

	const handleCreateAudienceFromContactsSave = useCallback(
		async (updates: AudiencePatch) => {
			if (!userId) return;
			const name = (updates.name ?? '').trim();
			if (!name) return;
			try {
				const created = await bridgewayApi.post<Audience>(
					`/api/user/${encodeURIComponent(userId)}/audiences/`,
					{ name, description: updates.description ?? '' },
				);
				audiencesTab.list.prependItem(created);
				setDraftAudience(null);
				setPanelKind(null);
				await contactsTab.handleBulkAddToAudience(created.id);
				setIsCreatingAudienceFromSelection(false);
			} catch (err) {
				logger.error('Failed to create audience and assign', { error: err });
				throw err;
			}
		},
		[audiencesTab.list, bridgewayApi, contactsTab, userId],
	);

	const handleClosePanel = useCallback(() => {
		if (panelKind === 'contact') {
			contactsTab.handleClosePanel();
		} else if (panelKind === 'audience') {
			audiencesTab.handleClosePanel();
			setIsCreatingAudienceFromSelection(false);
		}
	}, [audiencesTab, contactsTab, panelKind]);

	const isContactPanelActive =
		panelKind === 'contact' && (!!draftContact || !!selectedContactId);
	const isAudiencePanelActive =
		panelKind === 'audience' && (!!draftAudience || !!selectedAudienceId);
	const isDetailOpen = isContactPanelActive || isAudiencePanelActive;

	const containerRef = useRef<HTMLDivElement>(null);
	const { leftFraction, handleMouseDown, isDragging } = useResizePanel({
		containerRef,
	});

	const leftCardStyle = isDetailOpen
		? { flex: `${leftFraction} 1 0%` }
		: undefined;
	const rightCardStyle = isDetailOpen
		? { flex: `${1 - leftFraction} 1 0%` }
		: undefined;

	const panelTitle = (() => {
		if (isContactPanelActive) {
			if (draftContact) return 'New contact';
			return contactsTab.list.selectedItem?.name?.trim() ?? 'Contact';
		}
		if (isAudiencePanelActive) {
			if (draftAudience) return 'New audience';
			return audiencesTab.list.selectedItem?.name?.trim() ?? 'Audience';
		}
		return '';
	})();

	const panelDelete = (() => {
		if (
			isContactPanelActive &&
			!draftContact &&
			contactsTab.list.selectedItem
		) {
			const contact = contactsTab.list.selectedItem;
			return {
				onConfirm: contactsTab.handleContactDelete,
				ariaLabel: 'Delete contact',
				confirmTitle: 'Delete this contact?',
				confirmMessage: `${getDisplayName(contact)} will be removed from all audiences. This cannot be undone.`,
			};
		}
		if (
			isAudiencePanelActive &&
			!draftAudience &&
			audiencesTab.list.selectedItem
		) {
			const audience = audiencesTab.list.selectedItem;
			return {
				onConfirm: audiencesTab.handleAudienceDelete,
				ariaLabel: 'Delete audience',
				confirmTitle: 'Delete audience?',
				confirmMessage: `"${audience.name}" will be removed. Members will not be deleted. This cannot be undone.`,
			};
		}
		return undefined;
	})();

	const renderPanelBody = () => {
		if (isContactPanelActive) {
			if (draftContact) {
				return (
					<ContactDetail
						contact={draftContact}
						isSaving={contactsTab.isSavingContact}
						saveError={contactsTab.saveError}
						onSave={contactsTab.handleDraftSave}
						onCancel={handleClosePanel}
						isNew
					/>
				);
			}
			if (contactsTab.list.isLoadingItem) {
				return (
					<div className="flex h-full items-center justify-center">
						<span className="text-sm text-(--text-muted)">Loading…</span>
					</div>
				);
			}
			if (contactsTab.list.itemError || !contactsTab.list.selectedItem) {
				return (
					<div
						className="flex h-full flex-col items-center justify-center gap-2
							px-4 text-center"
					>
						<span className="text-sm text-(--beacon-error)">
							{contactsTab.list.itemError ?? 'Contact not found'}
						</span>
						<button
							type="button"
							onClick={() => void contactsTab.list.refresh()}
							className="text-sm text-(--text-primary) underline"
						>
							Try again
						</button>
					</div>
				);
			}
			return (
				<ContactDetail
					contact={contactsTab.list.selectedItem}
					isSaving={contactsTab.isSavingContact}
					saveError={contactsTab.saveError}
					onSave={contactsTab.handleContactSave}
				/>
			);
		}

		if (isAudiencePanelActive) {
			if (draftAudience) {
				const draftSave = isCreatingAudienceFromSelection
					? handleCreateAudienceFromContactsSave
					: audiencesTab.handleDraftSave;
				return (
					<AudienceDetail
						audience={draftAudience}
						userId={userId}
						isSaving={audiencesTab.isSavingAudience}
						saveError={audiencesTab.saveError}
						onSave={draftSave}
						onCancel={handleClosePanel}
						isNew
					/>
				);
			}
			if (audiencesTab.list.isLoadingItem) {
				return (
					<div className="flex h-full items-center justify-center">
						<span className="text-sm text-(--text-muted)">Loading…</span>
					</div>
				);
			}
			if (audiencesTab.list.itemError || !audiencesTab.list.selectedItem) {
				return (
					<div
						className="flex h-full flex-col items-center justify-center gap-2
							px-4 text-center"
					>
						<span className="text-sm text-(--beacon-error)">
							{audiencesTab.list.itemError ?? 'Audience not found'}
						</span>
						<button
							type="button"
							onClick={() => void audiencesTab.list.refresh()}
							className="text-sm text-(--text-primary) underline"
						>
							Try again
						</button>
					</div>
				);
			}
			return (
				<AudienceDetail
					audience={audiencesTab.list.selectedItem}
					userId={userId}
					isSaving={audiencesTab.isSavingAudience}
					saveError={audiencesTab.saveError}
					onSave={audiencesTab.handleAudienceSave}
				/>
			);
		}

		return null;
	};

	const onCreate =
		activeTab === 'contacts' ? handleCreateContact : handleCreateAudience;
	const createLabel = activeTab === 'contacts' ? 'New contact' : 'New audience';

	return (
		<div
			ref={containerRef}
			className="relative flex h-full w-full overflow-hidden"
		>
			<div
				className={cn(
					'glass-panel flex h-full min-h-0 min-w-0 flex-col overflow-hidden',
					!isDetailOpen && 'flex-1',
				)}
				style={leftCardStyle}
			>
				<div
					className="mx-auto flex min-h-0 w-full max-w-4xl min-w-0 flex-1
						flex-col"
				>
					<PeopleTabsHeader
						activeTab={activeTab}
						onCreate={onCreate}
						createLabel={createLabel}
						actions={
							activeTab === 'contacts' ? (
								<ImportContactsDialog
									onImport={contactsTab.handleImportCsv}
									isDisabled={!userId}
								/>
							) : undefined
						}
					/>
					<div className="flex min-h-0 min-w-0 flex-1 flex-col px-6 pb-6">
						<div
							className={cn(
								'flex min-h-0 min-w-0 flex-1 flex-col',
								activeTab !== 'contacts' && 'hidden',
							)}
						>
							<ContactsList
								contacts={
									contactsTab.list.searchResults ?? contactsTab.list.items
								}
								onContactClick={handleContactClick}
								onCreate={handleCreateContact}
								isLoading={contactsTab.list.isLoading}
								isLoadingMore={contactsTab.list.isLoadingMore}
								isSearching={contactsTab.list.isSearching}
								hasMore={
									contactsTab.list.searchResults === null &&
									contactsTab.list.hasMore
								}
								error={contactsTab.list.error}
								onLoadMore={contactsTab.list.loadMore}
								searchQuery={contactsTab.list.searchQuery}
								onSearchChange={contactsTab.list.setSearchQuery}
								audiences={audiencesTab.list.items}
								selectedContactIds={contactsTab.selectedContactIds}
								onSelectionChange={contactsTab.setSelectedContactIds}
								onClearSelection={contactsTab.clearSelection}
								onBulkAddToAudience={contactsTab.handleBulkAddToAudience}
								onBulkRemoveFromAudience={
									contactsTab.handleBulkRemoveFromAudience
								}
								onCreateAudienceForSelection={
									handleOpenCreateAudienceFromContacts
								}
								bulkError={contactsTab.bulkError}
								isBulkSaving={contactsTab.isBulkSaving}
							/>
						</div>
						<div
							className={cn(
								'flex min-h-0 min-w-0 flex-1 flex-col',
								activeTab !== 'audiences' && 'hidden',
							)}
						>
							<AudiencesList
								audiences={audiencesTab.list.items}
								onAudienceClick={handleAudienceClick}
								onCreate={handleCreateAudience}
								isLoading={audiencesTab.list.isLoading}
								isLoadingMore={audiencesTab.list.isLoadingMore}
								hasMore={audiencesTab.list.hasMore}
								error={audiencesTab.list.error}
								onLoadMore={audiencesTab.list.loadMore}
							/>
						</div>
					</div>
				</div>
			</div>
			{isDetailOpen && (
				<>
					<ResizeHandle onMouseDown={handleMouseDown} isDragging={isDragging} />
					<DetailPanelCard
						title={panelTitle}
						onClose={handleClosePanel}
						onDelete={panelDelete}
						style={rightCardStyle}
					>
						{renderPanelBody()}
					</DetailPanelCard>
				</>
			)}
		</div>
	);
});
